// What one unit of a group carries of a group cost, for the unit's costs tab.
//
// A group cost bound for the settlement has no split until a settlement picks
// its key, so its share is only known once it was split: billed right away
// (the cost popup stored the split) or settled in a confirmed group
// settlement. Until then the tab says the share follows in the settlement:
// never a guessed split (Lewis, 2026-10-08).

import { withBilledTarget } from "./chargeTarget.utils";
import { allocateCommonCost, buildingBreakdown } from "./engine";
import { existedSince, unitsExistingSince } from "./groupUnits.utils";
import { roundCents, toCents } from "./money.utils";
import {
  normalizeGroupSettlementCosts,
  withFrozenFigures,
} from "./settlementView.utils";
import {
  ApportionmentKey,
  ContractRef,
  DistributionMethod,
  Invoice,
  resolveDistributionMethod,
  Settlement,
  settlementAmount,
  SettlementDraftState,
} from "./types";

// How the share was found, for its label.
export type TGroupCostShareBasis =
  | { kind: "key"; keyName?: string; weight: number; totalWeight: number }
  | { kind: "consumption"; consumptionUnit?: "kwh" | "m3" }
  | { kind: "company" }
  | { kind: "amount" };

export type TGroupCostUnitShare =
  // The unit's part of the cost excl. VAT: its tenants', its vacant days' and
  // its owner's part together.
  | { status: "known"; amount: number; basis?: TGroupCostShareBasis }
  // Not split yet: a settlement still has to choose how.
  | { status: "pending" }
  // Split, but this viewer cannot redo it: an owner of some of the group's
  // units only gets their own units' readings or amounts.
  | { status: "unknown" };

interface IGroupUnit {
  id: number | string;
  archivedOn?: string;
}

const tenantSharePctOf = (invoice: Invoice): number =>
  invoice.target === "shared" ? invoice.tenantSharePct ?? 0 : 100;

// What a billed split rested on: a reading, an amount per unit, or a key.
const shareBasisOf = (
  distribution: NonNullable<Invoice["distribution"]>,
  unitShare: { weight: number; totalWeight: number },
): TGroupCostShareBasis => {
  if (distribution.method === "consumption") {
    return {
      kind: "consumption",
      consumptionUnit: distribution.consumptionUnit,
    };
  }
  if (distribution.method === "amount") return { kind: "amount" };
  return {
    kind: "key",
    keyName: distribution.keyName,
    weight: unitShare.weight,
    totalWeight: unitShare.totalWeight,
  };
};

// A group cost billed right away: the split the cost popup stored, redone the
// way the cost detail shows it (the units of the cost's period, then the
// units the stored split ran over that have left the group since).
export const billedGroupCostUnitShare = (params: {
  invoice: Invoice;
  unitId: string;
  groupUnits: IGroupUnit[];
}): TGroupCostUnitShare | undefined => {
  const { invoice, unitId, groupUnits } = params;
  const { distribution } = invoice;
  if (!distribution || (invoice.paymentSplits ?? []).length === 0) {
    return undefined;
  }
  const periodUnitIds = unitsExistingSince({
    units: groupUnits,
    from: invoice.periodFrom,
  }).map(unit => String(unit.id));
  const leftUnitIds = Object.keys(distribution.weights).filter(
    id => !periodUnitIds.includes(id),
  );
  const unitIds = [...periodUnitIds, ...leftUnitIds];
  const breakdown = buildingBreakdown({
    amount: invoice.amountExclVat,
    tenantSharePct: tenantSharePctOf(invoice),
    units: unitIds.map(id => ({ id, label: id })),
    contractsByUnit: {},
    weights: distribution.weights,
    period: { from: invoice.periodFrom, to: invoice.periodTo },
  });
  const unitShare = breakdown.units.find(unit => unit.unitId === unitId);
  if (!unitShare) return { status: "known", amount: 0 };

  return {
    status: "known",
    amount: unitShare.unitSlice,
    basis: shareBasisOf(distribution, unitShare),
  };
};

// A group cost settled in a confirmed group settlement: the settlement's own
// split of that cost, redone from its frozen snapshot with the engine's
// allocateCommonCost, so the unit shows the cents the settlement charged.
export const settledGroupCostUnitShare = (params: {
  invoice: Invoice;
  unitId: string;
  settlement: Settlement;
  // The group's costs, for the other costs the settlement held: a metering
  // company's amounts cover a whole category.
  groupInvoices: Invoice[];
  // The group's live keys, for a snapshot that froze no weights.
  keys: ApportionmentKey[];
  // The group's units, for a snapshot that froze none.
  groupUnits: IGroupUnit[];
}): TGroupCostUnitShare => {
  const { unitId, settlement, keys } = params;
  const { snapshot } = settlement;
  if (!snapshot) return { status: "unknown" };

  // Every cost with the figures the settlement froze, before the billed
  // target is read from them: a cost edited since splits as it was settled.
  const settledIds = new Set(snapshot.invoiceIds);
  const settledCosts = normalizeGroupSettlementCosts(
    params.groupInvoices
      .filter(invoice => settledIds.has(invoice.id))
      .map(invoice => withFrozenFigures(invoice, snapshot)),
    settlement,
  ).map(withBilledTarget);
  const invoice =
    settledCosts.find(cost => cost.id === params.invoice.id) ??
    withBilledTarget(
      normalizeGroupSettlementCosts(
        [withFrozenFigures(params.invoice, snapshot)],
        settlement,
      )[0],
    );
  if (invoice.scope !== "common") return { status: "unknown" };

  const { category } = invoice;
  // Snapshots from before the distribution was frozen were settled by key.
  const method = snapshot.distributionMethod
    ? resolveDistributionMethod(category, snapshot.distributionMethod)
    : "key";
  // A split by readings or a metering company: each unit's part as the
  // settlement froze it (Y16), also for an owner who gets only their own
  // units' readings and amounts. Without it (an older snapshot), such an
  // owner's split over the others cannot be redone.
  const frozenShare = snapshot.unitSharesByInvoice?.[invoice.id];
  if (method !== "key" && frozenShare) {
    return {
      status: "known",
      amount: frozenShare[unitId] ?? 0,
      basis: { kind: method === "company" ? "company" : "consumption" },
    };
  }
  if (snapshot.viewerPart && method !== "key") return { status: "unknown" };

  const keyId = snapshot.selectedKeyId?.[category];
  const liveKey = keys.find(key => key.id === keyId);
  const frozenWeights = keyId ? snapshot.keySnapshots?.[keyId] : undefined;
  // Frozen weights carry an equal key as 1 per unit (see freezeKeyWeights).
  const key = frozenWeights
    ? { isEqual: false, weights: frozenWeights }
    : liveKey;

  const units: IGroupUnit[] =
    snapshot.allGroupUnits ?? snapshot.groupUnits ?? params.groupUnits;
  const archivedOn: Record<string, string | undefined> = Object.fromEntries(
    units.map(unit => [String(unit.id), unit.archivedOn]),
  );
  const existedForCost = (id: string) =>
    existedSince({ archivedOn: archivedOn[id], from: invoice.periodFrom });
  // The leases the settlement settled, as far as the split needs them: which
  // unit each one is on. Their days only divide a unit's part among its
  // tenants and its vacancy, which stay with the unit.
  const contracts: ContractRef[] = snapshot.perContract.map(result => ({
    id: result.contractId,
    unitId: result.unitId,
    unitCode: result.unitCode,
    tenantName: result.tenantName,
    startDate: invoice.periodFrom,
    endDate: invoice.periodTo,
  }));
  const unitIds = units.map(unit => String(unit.id));
  const groupUnitIds = [
    ...new Set([...unitIds, ...contracts.map(contract => contract.unitId)]),
  ];
  const companyTotal = settledCosts
    .filter(cost => cost.scope === "common" && cost.category === category)
    .reduce((sum, cost) => sum + settlementAmount(cost), 0);

  const allocation = allocateCommonCost({
    invoice,
    method,
    key,
    consumption: snapshot.consumption?.[category] ?? {},
    buildingTotal: snapshot.buildingTotal?.[category] ?? 0,
    excessMode: snapshot.excessMode?.[category] ?? "landlord",
    companyAmounts: snapshot.companyAmounts?.[category] ?? {},
    companyTotal,
    contracts: contracts.filter(contract => existedForCost(contract.unitId)),
    costUnitIds: groupUnitIds.filter(existedForCost),
    allocateUnitIds: unitIds.filter(existedForCost),
  });
  const tenantsPart = contracts
    .filter(contract => contract.unitId === unitId)
    .reduce(
      (sum, contract) => sum + (allocation.perContract[contract.id] ?? 0),
      0,
    );
  const amount = roundCents(
    tenantsPart +
      (allocation.vacancyByUnit[unitId] ?? 0) +
      (allocation.baseByUnit[unitId] ?? 0),
  );

  if (method === "consumption") {
    return { status: "known", amount, basis: { kind: "consumption" } };
  }
  if (method === "company") {
    return { status: "known", amount, basis: { kind: "company" } };
  }
  // The key's weights over the units the cost was split over; one that gives
  // none of them any weight splits equally, like the engine.
  const splitUnitIds = groupUnitIds.filter(existedForCost);
  const weightOf = (id: string) =>
    !key || key.isEqual ? 1 : key.weights[id] ?? 0;
  const splitsEqually = !splitUnitIds.some(id => weightOf(id) > 0);
  // A unit the cost was not split over (archived before its period) weighs
  // nothing in it, whatever the key gives it.
  const weightIfSplit = splitsEqually ? 1 : weightOf(unitId);
  const weight = splitUnitIds.includes(unitId) ? weightIfSplit : 0;
  const totalWeight = splitUnitIds.reduce(
    (sum, id) => sum + (splitsEqually ? 1 : weightOf(id)),
    0,
  );
  return {
    status: "known",
    amount,
    basis: {
      kind: "key",
      keyName: keyId ? snapshot.keyNames?.[keyId] ?? liveKey?.name : undefined,
      weight,
      totalWeight,
    },
  };
};

// The share of every group cost on one unit: settled in a confirmed group
// settlement, else billed right away, else still to be split.
export const groupCostUnitShare = (params: {
  invoice: Invoice;
  unitId: string;
  // The confirmed settlement of the group that holds this cost, if any.
  settlement?: Settlement;
  groupInvoices: Invoice[];
  keys: ApportionmentKey[];
  groupUnits: IGroupUnit[];
}): TGroupCostUnitShare => {
  const { invoice, unitId, settlement, groupUnits } = params;
  if (settlement) {
    return settledGroupCostUnitShare({ ...params, settlement });
  }
  return (
    billedGroupCostUnitShare({ invoice, unitId, groupUnits }) ?? {
      status: "pending",
    }
  );
};

// ----- Whether a building cost applies to a unit -----

// Whether a split still to be made will leave the unit out for certain
// (Lewis, 2026-10-16). Only a key can say so: a key the group's draft
// settlement holding the cost chose for its category, that gives the unit no
// weight while it gives others some, or a unit archived before the cost's
// period, which no key split reaches. A reading or a metering company's
// amount is not known before the settlement, nor is a key no draft chose
// yet (the settlement starts from an equal split, which includes the unit)
// or a chosen key that is gone.
export const pendingSplitLeavesOutUnit = (params: {
  invoice: Invoice;
  unitId: string;
  // The group's live keys: a draft follows them until it is confirmed.
  keys: ApportionmentKey[];
  groupUnits: IGroupUnit[];
  // The group's own settlements; its drafts that hold the cost carry the
  // key they chose for its category.
  groupSettlements: Settlement[];
}): boolean => {
  const { invoice, unitId, keys, groupUnits } = params;
  if (invoice.scope !== "common") return false;
  const { category } = invoice;
  const drafts = params.groupSettlements
    .map(settlement =>
      settlement.status === "draft" && settlement.scope === "group"
        ? settlement.draftState
        : undefined,
    )
    .filter(
      (draft): draft is SettlementDraftState =>
        !!draft?.selectedInvoiceIds?.[invoice.id] && !draft.departure,
    );
  const choices =
    drafts.length > 0
      ? drafts.map(draft => ({
          method: resolveDistributionMethod(category, draft.distributionMethod),
          keyId: draft.selectedKeyId?.[category],
        }))
      : [{ method: resolveDistributionMethod(category), keyId: undefined }];

  const existedForCost = (id: string) =>
    existedSince({
      archivedOn: groupUnits.find(unit => String(unit.id) === id)?.archivedOn,
      from: invoice.periodFrom,
    });
  const splitUnitIds = groupUnits
    .map(unit => String(unit.id))
    .filter(existedForCost);
  const leavesOut = (choice: {
    method: DistributionMethod;
    keyId?: string;
  }): boolean => {
    if (choice.method !== "key") return false;
    if (!existedForCost(unitId)) return true;
    const key = keys.find(candidate => candidate.id === choice.keyId);
    if (!key || key.isEqual) return false;
    const weightOf = (id: string) => key.weights[id] ?? 0;
    // A key that gives none of the units any weight splits equally.
    if (!splitUnitIds.some(id => weightOf(id) > 0)) return false;
    return !(weightOf(unitId) > 0);
  };
  return choices.every(leavesOut);
};

// Whether a building's cost belongs on a unit's page (Lewis, 2026-10-16): not
// when its share is known to be € 0,00 (the settlement or the billed split
// gave the unit nothing, or left it out), nor when the split still to be
// made leaves the unit out for certain (see pendingSplitLeavesOutUnit). A
// share this viewer cannot redo stays: it may well be the unit's.
export const groupCostAppliesToUnit = (params: {
  share: TGroupCostUnitShare;
  invoice: Invoice;
  unitId: string;
  keys: ApportionmentKey[];
  groupUnits: IGroupUnit[];
  groupSettlements: Settlement[];
}): boolean => {
  const { share } = params;
  switch (share.status) {
    case "known":
      return toCents(share.amount) !== 0;
    case "pending":
      return !pendingSplitLeavesOutUnit(params);
    case "unknown":
      return true;
  }
};
